import { useCallback, useEffect, useRef, useState } from "react";
import { X, Search, Loader2, AlertCircle } from "lucide-react";
import client from "@/lib/api";

const PAGE_SIZE = 24;
const DEBOUNCE_MS = 320;

/**
 * Giphy-powered GIF picker.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - onSelect: (gif) => void  // gif = { id, title, url, preview }
 *
 * Renders a search bar + masonry-ish 2/3-column grid of GIFs with infinite
 * scroll. Default view shows trending. Types debounced to 320ms.
 */
export default function GifPickerModal({ open, onClose, onSelect }) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [gifs, setGifs] = useState([]);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const sentinelRef = useRef(null);
  const scrollRef   = useRef(null);
  const inputRef    = useRef(null);

  // Debounce query
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [query, open]);

  // Reset & fetch first page whenever debounced query changes
  useEffect(() => {
    if (!open) return;
    setGifs([]); setOffset(0); setTotal(0); setError(null);
    fetchPage(0, debounced, /* replace */ true);
  }, [debounced, open]);

  // Focus search input when opened
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 60);
  }, [open]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const fetchPage = useCallback(async (nextOffset, q, replace) => {
    setLoading(true);
    setError(null);
    try {
      const endpoint = q ? "/gifs/search" : "/gifs/trending";
      const params = q ? { q, limit: PAGE_SIZE, offset: nextOffset } : { limit: PAGE_SIZE, offset: nextOffset };
      const r = await client.get(endpoint, { params });
      setGifs((prev) => replace ? r.data.results : [...prev, ...r.data.results]);
      setOffset(nextOffset + (r.data.count || r.data.results.length));
      setTotal(r.data.total_count || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "GIF fetch failed");
    } finally {
      setLoading(false);
    }
  }, []);

  // Infinite-scroll sentinel
  useEffect(() => {
    if (!open || loading || !sentinelRef.current) return;
    const hasMore = offset < total;
    if (!hasMore) return;
    const obs = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && !loading) {
        fetchPage(offset, debounced, false);
      }
    }, { root: scrollRef.current, rootMargin: "150px", threshold: 0 });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [open, loading, offset, total, debounced, fetchPage]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-[rgba(2,6,15,0.82)] backdrop-blur-sm p-3 md:p-6"
      onClick={onClose}
      data-testid="gif-picker-modal"
    >
      <div
        className="panel corner-frame relative w-full max-w-3xl h-[80vh] max-h-[720px] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b border-[var(--t-border)]">
          <div className="min-w-0">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--t-cyan)]">// GIF PICKER</div>
            <div className="font-display text-base text-[var(--t-text)] uppercase tracking-[0.06em]">Insert a GIF</div>
          </div>
          <div className="ml-auto relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[var(--t-text-muted)] pointer-events-none" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search GIFs…"
              className="w-full pl-8 pr-3 py-2 text-xs font-mono bg-[#050912] border border-[var(--t-border)] focus:border-[var(--t-blue)] outline-none text-[var(--t-text)] placeholder:text-[var(--t-text-muted)] transition"
              data-testid="gif-picker-search"
            />
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center border border-[var(--t-border)] text-[var(--t-text-muted)] hover:text-[var(--t-cyan)] hover:border-[var(--t-blue)] transition flex-shrink-0"
            data-testid="gif-picker-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Trending / results label */}
        <div className="px-4 py-2 border-b border-[var(--t-border)] flex items-center justify-between">
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--t-text-muted)]">
            {debounced ? `// results for "${debounced}"` : "// trending"}
          </span>
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--t-text-muted)]">
            powered by giphy
          </span>
        </div>

        {/* Grid */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto p-3"
          data-testid="gif-picker-grid"
        >
          {error && (
            <div className="p-4 flex items-start gap-2 text-sm" data-testid="gif-picker-error">
              <AlertCircle className="w-4 h-4 text-[var(--t-red)] flex-shrink-0 mt-0.5" />
              <span className="text-[var(--t-red)] font-mono text-xs">{typeof error === "string" ? error : JSON.stringify(error)}</span>
            </div>
          )}
          {!error && gifs.length === 0 && !loading && (
            <div className="p-8 text-center font-mono text-xs text-[var(--t-text-muted)] uppercase tracking-[0.25em]">
              // no gifs found
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {gifs.map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => { onSelect?.(g); onClose?.(); }}
                className="group relative aspect-square overflow-hidden border border-[var(--t-border)] hover:border-[var(--t-blue-glow)] transition bg-[#050912]"
                data-testid={`gif-item-${g.id}`}
                title={g.title}
              >
                {g.preview?.url && (
                  <img
                    src={g.preview.url}
                    alt={g.title || "GIF"}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-200"
                  />
                )}
                <span
                  aria-hidden
                  className="absolute inset-0 border border-transparent group-hover:border-[var(--t-cyan)] group-hover:shadow-[inset_0_0_18px_rgba(102,230,255,0.35)] transition"
                />
              </button>
            ))}
          </div>
          {loading && (
            <div className="flex items-center justify-center py-6 text-[var(--t-text-muted)] font-mono text-xs tracking-[0.25em] uppercase" data-testid="gif-picker-loading">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> loading…
            </div>
          )}
          <div ref={sentinelRef} className="h-2" />
        </div>
      </div>
    </div>
  );
}
